import { useEffect, useState } from "react";
import { FileCheck2 } from "lucide-react";
import useCountUp from "@/lib/useCountUp";

/**
 * Animated report dashboard — risk score, progress lines, photo evidence
 * blocks, and redacted sample sections with a "Litigation Ready" tag.
 */
export default function ClaimsReportsVisual() {
  const { ref, value: score } = useCountUp({ end: 92, duration: 2200 });
  const [flip, setFlip] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setFlip((p) => !p), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200 bg-[#fafafa] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.12)] p-6"
    >
      <div className="absolute inset-0 btr-blueprint-overlay opacity-20" />

      {/* Header bar */}
      <div className="relative flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">Risk Report</div>
          <div className="font-display font-bold text-[#111114] text-sm mt-1">Case BTR-2026-0418</div>
        </div>
        <div
          className={`px-2.5 py-1 rounded-sm text-[9px] font-display font-extrabold tracking-[0.25em] uppercase border transition-colors ${
            flip ? "bg-[#a2020b] text-white border-[#a2020b]" : "bg-[#a2020b]/10 text-[#a2020b] border-[#a2020b]/40"
          }`}
        >
          Litigation Ready
        </div>
      </div>

      <div className="h-[1px] bg-zinc-200 mt-3" />

      {/* Top grid: Score + Photo blocks */}
      <div className="relative mt-4 grid grid-cols-3 gap-3">
        {/* Score card */}
        <div className="bg-white rounded-md border border-zinc-200 p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-display">Risk Score</div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="font-display font-extrabold text-[#111114] text-3xl">{score}</span>
            <span className="text-zinc-400 text-[10px] font-body">/ 100</span>
          </div>
          <div className="h-1.5 mt-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#a2020b] to-[#c0030e] transition-all duration-[1800ms]"
              style={{ width: `${score}%` }}
            />
          </div>
        </div>

        {/* Photo blocks */}
        <div className="col-span-2 grid grid-cols-3 gap-2">
          {[
            "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&q=60&auto=format&fit=crop",
            "https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=300&q=60&auto=format&fit=crop",
            "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=300&q=60&auto=format&fit=crop",
          ].map((u, i) => (
            <div key={i} className="relative rounded overflow-hidden border border-zinc-200 aspect-square">
              <img src={u} alt="" className="absolute inset-0 w-full h-full object-cover grayscale-[0.2]" loading="lazy" decoding="async" width="300" height="300" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#a2020b]/40 to-transparent mix-blend-multiply" />
              <div className="absolute bottom-1 left-1 text-[7px] font-display font-bold tracking-[0.15em] text-white uppercase px-1 py-0.5 bg-black/40 rounded-sm">
                EV-0{i + 1}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Estimate lines */}
      <div className="relative mt-4 bg-white rounded-md border border-zinc-200 p-3">
        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-display">Estimate Summary</div>
        <div className="mt-2 space-y-1.5">
          {[
            { label: "Exterior Envelope", value: "$128,400", w: 84 },
            { label: "Roof System", value: "$96,200", w: 62 },
            { label: "Interior Finishes", value: "$72,800", w: 48 },
            { label: "MEP & Systems", value: "$54,100", w: 36 },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-3 text-[10px] font-body">
              <span className="w-28 text-zinc-700 truncate">{r.label}</span>
              <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#a2020b] to-[#a2020b]/40 transition-all duration-[1500ms]"
                  style={{ width: `${r.w}%` }}
                />
              </div>
              <span className="w-16 text-right text-zinc-600 font-display font-semibold">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Redacted notes */}
      <div className="relative mt-3 grid grid-cols-2 gap-2">
        {[1, 2].map((k) => (
          <div key={k} className="bg-white rounded-md border border-zinc-200 p-2.5">
            <div className="text-[8px] tracking-[0.3em] uppercase text-zinc-500 font-display">
              Strategic Note
            </div>
            <div className="mt-1.5 space-y-1">
              {[100, 82, 68, 92].map((w, i) => (
                <div key={i} className="h-1 rounded-full overflow-hidden bg-zinc-100 relative">
                  <div
                    className={`h-full ${i === 1 ? "bg-[#a2020b]/80" : "bg-zinc-300"}`}
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Verified chip */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white border border-[#a2020b]/30 rounded-full px-3 py-1.5 shadow-sm">
        <FileCheck2 className="w-3.5 h-3.5 text-[#a2020b]" />
        <span className="text-[10px] font-display font-bold tracking-[0.2em] uppercase text-[#a2020b]">
          Report Verified
        </span>
      </div>

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-[2px] bg-[#a2020b] opacity-60"
        style={{ animation: "btr-scan 5.5s ease-in-out infinite", boxShadow: "0 0 12px #a2020b" }}
      />
    </div>
  );
}
